/* Globalne tło: KONSTELACJA SIECI — węzły + linie + pulsujące punkty + pakiety.
   Rendered w Layout, pod treścią. Ukryte na panelu admina. */
import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import { ADMIN_PATH } from "@/lib/adminPath";

// Deterministic pseudo-random (żeby konstelacja była taka sama przy każdym renderze)
function seededRandom(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function buildConstellation(seed = 42, nodeCount = 55, maxDist = 18, xMin = 0, xMax = 100) {
  const rand = seededRandom(seed);
  const nodes = [];
  const xSpan = xMax - xMin;
  for (let i = 0; i < nodeCount; i++) {
    nodes.push({
      id: i,
      x: xMin + rand() * xSpan,
      y: rand() * 100,
      pulse: i % 4 === 0,        // co 4-ty węzeł pulsuje
      accent: i % 9 === 0,       // co 9-ty jest czerwony
      pulseDelay: rand() * 4,
    });
  }
  // Krawędzie: łączymy węzły w promieniu maxDist
  const edges = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < maxDist) {
        edges.push({
          from: nodes[i],
          to: nodes[j],
          dist: d,
          opacity: Math.max(0.05, 0.35 * (1 - d / maxDist)),
        });
      }
    }
  }
  // Wybieramy kilka losowych krawędzi na trasy pakietów
  const packetPaths = [];
  for (let k = 0; k < 8; k++) {
    const e = edges[Math.floor(rand() * edges.length)];
    if (e) {
      packetPaths.push({
        from: e.from,
        to: e.to,
        duration: 12 + rand() * 8,
        delay: rand() * 10,
        accent: rand() > 0.6,
      });
    }
  }
  return { nodes, edges, packetPaths };
}

// Seedy per-route — każda podstrona ma unikalny układ konstelacji.
// Można też ograniczyć obszar (xRange), np. Home ma mapę Polski po prawej
// więc konstelację lokujemy tylko po lewej stronie.
const ROUTE_CONFIG = {
  "/":                 { seed: 12,  xRange: [0, 52] },
  "/abonamenty":       { seed: 27 },
  "/internet-mobilny": { seed: 217 },
  "/zamow-sim":        { seed: 63 },
  "/przenies-numer":   { seed: 88 },
  "/kontakt":          { seed: 105 },
  "/regulamin":        { seed: 131 },
  "/rodo":             { seed: 154 },
  "/polityka-cookies": { seed: 172 },
};

export default function RadioWavesBackground() {
  const { pathname } = useLocation();
  const cfg = ROUTE_CONFIG[pathname] || { seed: 42 };
  const [xMin, xMax] = cfg.xRange || [0, 100];

  const { nodes, edges, packetPaths } = useMemo(
    () => buildConstellation(cfg.seed, 55, 18, xMin, xMax),
    [cfg.seed, xMin, xMax]
  );

  if (pathname.startsWith(`/${ADMIN_PATH}`)) return null;

  return (
    <div
      aria-hidden="true"
      className="rm-radio-bg"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ display: "block" }}
      >
        {/* Krawędzie sieci */}
        {edges.map((e, i) => (
          <line
            key={`e-${i}`}
            x1={e.from.x}
            y1={e.from.y}
            x2={e.to.x}
            y2={e.to.y}
            stroke="#ffffff"
            strokeWidth="0.08"
            strokeOpacity={e.opacity}
            className="rm-net-edge"
            style={{ animationDelay: `${(i * 0.05) % 4}s` }}
          />
        ))}

        {/* Węzły */}
        {nodes.map((n) => (
          <circle
            key={`n-${n.id}`}
            cx={n.x}
            cy={n.y}
            r={n.pulse ? 0.38 : 0.28}
            fill={n.accent ? "#dc2626" : "#ffffff"}
            fillOpacity={n.accent ? 0.9 : 0.5}
            className={n.pulse ? "rm-net-node rm-net-node-pulse" : "rm-net-node"}
            style={{
              animationDelay: `${n.pulseDelay}s`,
            }}
          />
        ))}

        {/* Pakiety danych — poruszają się po wybranych krawędziach (SMIL) */}
        {packetPaths.map((p, i) => (
          <circle
            key={`p-${i}`}
            r="0.28"
            fill={p.accent ? "#ef4444" : "#ffffff"}
            fillOpacity="0.9"
            cx={p.from.x}
            cy={p.from.y}
          >
            <animate
              attributeName="cx"
              from={p.from.x}
              to={p.to.x}
              dur={`${p.duration}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="cy"
              from={p.from.y}
              to={p.to.y}
              dur={`${p.duration}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;0.9;0.9;0"
              keyTimes="0;0.15;0.85;1"
              dur={`${p.duration}s`}
              begin={`${p.delay}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </svg>
    </div>
  );
}
